import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnInit, NgZone, OnDestroy} from '@angular/core';
import * as DetectRTC from 'detectrtc';
import { AuthService } from '../../../app/services/auth.service';
import { RecordingHelperService } from './../../services/recording-helper.service';
import { ActivityViewHelperService } from './../../services/activity-view-helper.service';
import { AudioPlayerService }  from './../../services/audio-player.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'recording',
    templateUrl: './recording.template.html',
    styleUrls: ['./recording.style.scss'],
    standalone: false
})
export class Recording implements OnInit, OnDestroy{
  recorderState : string = 'initial'
  elapsedTime: string = "00:00:00";
  elapsedLeftTime: number = 0;
  timer: any;
  audioSeeker: string;
  audioDuration: string;
  attemptNumber : number;
  mediaStream: any;
  segment: string;
  private mediaRecorder: any;
  private partitionKey: string;
  private recordsToSend = [];
  private recordingFileExtension: string;
  private iterationCounter: number = 0;
  private recordUploadInProcess = false;
  private isRecordingSaved = false;
  private recordingTimeLimit = 20 * 60; // 20 minutes in seconds
  private timesliceSize = 30; // in seconds
  private componentDestroyed = false;
  private receivedLastChunk = false;
  private pushXEventSubscription;
  private orgId: string;
  private awaitingAudioStream: boolean;
  private audioElement: HTMLAudioElement;
  /** Event handler for `loadedmetadata` event on the audio element */
  private readonly metadataLoadedListener = () => {
    this.audioDuration = this.calculateRecordingTimer(
      this.audioElement.duration,
      true
    );
  };
  /** Event handler for `timeupdate` event on the audio element */
  private readonly timeUpdateListener = () => {
    //Audio Seeker update while playing audio
    this.audioSeeker = this.calculateRecordingTimer(this.audioElement.currentTime);
    if (this.audioElement.currentTime === this.audioElement.duration) {
      this.recorderState = 'stopped';
    }
  };
  private recordedAudioDuration: number = 0;
  private recordingStartTime: number;

  @ViewChild('showRecordingErrorModalBtn') showRecordingErrorModalBtn : ElementRef;
  @ViewChild('recordingElement') recordingElement : ElementRef;
  
  @Input('productMeta') productMeta: any;
  @Input('activityId') activityId: any;

  @Input('analytics') analytics : any;
  @Output() recordingStarted = new EventEmitter<boolean>(); 
  @Output() recordingStopped = new EventEmitter<boolean>();
  @Output() recordingSavedOrSubmitted = new EventEmitter<any>();
  stopAllOtherAudiosOnPlayingOneAudioSubscription : any;
  stopOtherAudioActivitiesSubscription : Subscription;

  constructor(
    private authService : AuthService,
    private route: ActivatedRoute,
    private ngZone : NgZone,
    private recordingHelperService : RecordingHelperService,
    private activityViewHelperService : ActivityViewHelperService,
    private audioPlayerService : AudioPlayerService) { }


  ngOnInit() {
    this.resetState();
    // NEMO-11467, NEMO-11684: Hardcoding play button color for "adult" segment.
    const userRole = this.authService.getUserData() ? this.authService.getUserData().role : "";
    this.segment = userRole === 'student' && this.activityViewHelperService.getSegment() ? this.activityViewHelperService.getSegment() : "adult";
    if(this.analytics && this.analytics.evaluation) {
      this.attemptNumber = this.analytics.evaluation.length;
    } else {
      this.attemptNumber = 0;
    }

    // Audio player implementation using HTMLAudioElement.
    this.audioElement = new Audio("");
    this.audioElement.addEventListener("loadedmetadata", this.metadataLoadedListener);
    this.audioElement.addEventListener("timeupdate", this.timeUpdateListener);

    this.stopAllOtherAudiosOnPlayingOneAudioSubscription = this.activityViewHelperService.stopAllOtherAudiosOnPlayingOneAudio.subscribe((attemptNumber) => {
      //Stop the audio playing when already submitted audio is played 
      if(this.attemptNumber != attemptNumber) {
        if(this.recorderState === "playing") {
          this.stopPlayingAudio();
        }
      }
    })
    this.stopOtherAudioActivitiesSubscription = this.audioPlayerService.audioPromptPlayEvent.subscribe( () => {
      if (this.recorderState === "playing") {
          this.stopPlayingAudio();
      } else if (this.recorderState == 'recording') {
          this.stopRecording();
      }
    })
    this.recordingHelperService.setupPushXEventListener().catch((e) => { throw e; });
  }

  //Start audio Recording
  startRecording() {
    if (this.recorderState === "recording" || this.awaitingAudioStream) {
      return;
    }
    this.awaitingAudioStream = true;
    this.audioPlayerService.pauseAudioPromptPlayer();
    this.activityViewHelperService.stopAllOtherAudiosOnPlayingOneAudio.next(this.attemptNumber);
    DetectRTC.load(() => {
      if (window['MediaRecorder'] === undefined || DetectRTC.hasMicrophone === false) { // MediaRecorder is supported or not
          this.showRecordingErrorModalBtn.nativeElement.click(); //Show error popup if not supported
          this.awaitingAudioStream = false;
      } else { //Else start Recording
          const mediaConstraints = {
              audio: true
          };
          navigator.mediaDevices
            .getUserMedia(mediaConstraints)
            .then(this.getUserMediaSuccessCallback.bind(this), this.getUserMediaErrorCallback.bind(this));
      }
    });
  }

  //Stop Audio Recording
  stopRecording (errorOccured = false) {
    if (this.awaitingAudioStream) {
      // Don't stop audio recording when waiting for browser promises
      return;
    }
    this.recordedAudioDuration = (new Date().getTime() - this.recordingStartTime) / 1000;
    this.recorderState = errorOccured ? 'initial' : 'processing';
    this.resetState();
  }

  private resetState() {
    clearInterval(this.timer);
    this.mediaRecorder && this.mediaRecorder.stop();
    this.mediaRecorder = null;
    this.mediaStream && this.mediaStream.getTracks().forEach( t => { t.stop(); });
    this.mediaStream = null;
  }

  private startTimer() {
    this.ngZone.run(()=>{
      let differenceOfTime = 0;
      let recordingAlertLmit = this.recordingTimeLimit - 30; // 30 sec before to show alert

      this.elapsedTime = "00:00:00";
      this.elapsedLeftTime = 0;
      this.iterationCounter = 0;
      this.isRecordingSaved = false;
      this.recordingStartTime = new Date().getTime(); // Store recording start time

      this.timer = setInterval(() => {
        differenceOfTime = Math.round((new Date().getTime() - this.recordingStartTime) / 1000); // Current Time - Stored Started Time in sec
        this.elapsedTime = this.calculateRecordingTimer(differenceOfTime); //Recording elapsed time
        
        if (differenceOfTime >= this.recordingTimeLimit) { //If audio recorded is equal or greater than record limit
          this.stopRecording(); //Stop Recording 
        } else if (differenceOfTime >= recordingAlertLmit) { //If audio recorded is equal or greater than alert record limit
          this.elapsedLeftTime = Math.round((this.recordingTimeLimit - differenceOfTime)); //Show counter for left time
        }
      }, 1000)
      this.recordingHelperService.initializeForUplaod(this.route.parent.snapshot.params.spaceId).then( orgId => {
        this.orgId = orgId;
      }).catch((err) => {
        this.getUserMediaErrorCallback(err);
      });
    });
  }

  //getUserMedia Success CallBack  
  getUserMediaSuccessCallback(audioStream) {
    this.ngZone.run(() => {
      this.recorderState = "recording"; //Change the recorder State
      this.recordingStarted.emit(true); //emit start Recording event to parent
      if(this.recordingElement) {
        this.recordingElement.nativeElement.focus();
      }

      this.mediaStream = audioStream;
      //Initialise the MediaRecorder
      const MediaRecorder = window['MediaRecorder'];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      this.recordingFileExtension = MediaRecorder.isTypeSupported('audio/webm') ? 'webm' : 'mp4';
      this.mediaRecorder = new MediaRecorder(audioStream, { mimeType });
      this.mediaRecorder.ondataavailable = this.processRecordingBlob.bind(this);
      this.mediaRecorder.onstart = this.startTimer.bind(this);
      this.receivedLastChunk = false;
      this.mediaRecorder.start(this.timesliceSize * 1000); // timeslice needs to be in ms
      this.awaitingAudioStream = false;
    });
  }

  //getUserMedia Error CallBack  
  getUserMediaErrorCallback(error) {
    this.awaitingAudioStream = false;
    if (this.recorderState === 'recording') {
      this.stopRecording(true);
    } else {
      this.recorderState = 'initial';
    }
    // In case of error, discard the chunk
    this.receivedLastChunk = true;
    this.showRecordingErrorModalBtn.nativeElement.click();
  }

  private async processRecordingBlob(blobEvent) {
    try {
      if (this.componentDestroyed || this.receivedLastChunk) {
        this.stopRecording();
        return;
      }
      if (!blobEvent.data || blobEvent.data.size === 0) return;

      // When the recorder is not in recording state, consider it the last chunk
      this.receivedLastChunk = this.recorderState !== 'recording';

      const blobsArray = [];
      // If blob size is more than 750 KB, split the blob into multiple blobs
      if (blobEvent.data.size < 750 * 1024) {
        blobsArray.push(blobEvent.data);
      } else {
        let processedBlobSize = 0;
        while (processedBlobSize !== blobEvent.data.size) {
          let endIndex = processedBlobSize + 750 * 1024;
          if (endIndex > blobEvent.data.size) {
            endIndex = blobEvent.data.size;
          }
          blobsArray.push(blobEvent.data.slice(processedBlobSize, endIndex));
          processedBlobSize = endIndex;
        }
      }
      for(let index = 0; index < blobsArray.length; index++) {
        this.iterationCounter++;
        this.prepareKinesisRecord(blobsArray[index], this.iterationCounter, (index + 1) === blobsArray.length, blobsArray.length > 1).catch((error) => {
          this.getUserMediaErrorCallback(error);
        });
        // When chunk is splitted, wait 1 sec before sending the next one
        await new Promise(res => setTimeout(res, 1000));
      }
      // Stop recording when a blob is received AND:
      //  - UI is NOT in recording state
      // OR UI is in recording state AND the iterations had reached the allowed limit
      if (this.recorderState !== 'recording' ||
          (this.recorderState === 'recording' && this.iterationCounter >= this.recordingTimeLimit/this.timesliceSize) ) {
        this.stopRecording();
      }
    } catch (error) {
      this.getUserMediaErrorCallback(error);
    }
  }

  private async prepareKinesisRecord(audioBlob, iteration, isLastBlob, audioChunkSpliced) {
    if (iteration > this.recordingTimeLimit/this.timesliceSize) {
      this.stopRecording();
      return;
    }
    if (!this.partitionKey) {
      const userData = this.authService.getUserData();
      const productPublicSubmissionPath = this.productMeta.paths['public-submissions'];
      const productPathForKey = productPublicSubmissionPath.substring(productPublicSubmissionPath.indexOf('/submissions/') + 13);
      this.partitionKey = `${productPathForKey}/${userData.extUserId}/${this.activityId.replace(/\//g, '')}`;
      const classId = this.activityViewHelperService.getClass();
      if(classId) {
        this.partitionKey+= `/${classId}`
      }
      this.partitionKey+= `/${this.attemptNumber}`;
    }

    const isLastPart = isLastBlob && (iteration === this.recordingTimeLimit/this.timesliceSize || this.recorderState !== 'recording');
    const recordData = JSON.stringify({
      // Metadata
      M: { I: iteration, FE: this.recordingFileExtension, CR: isLastPart ? 1 : 0, ACS: audioChunkSpliced ? 1: 0, OID: this.orgId },
      // Audio data
      D: await this.convertBlobToBase64(audioBlob),
    });

    this.recordsToSend.push(recordData);
    this.sendRecords().catch((error) => {
      this.recordsToSend = [];
      this.getUserMediaErrorCallback(error);
    });
  }

  // Post records to Kinesis in a recursive manner
  private async sendRecords() {
    if (this.recordsToSend.length !== 0 && !this.recordUploadInProcess) {
      this.recordUploadInProcess = true;

      const recordData = this.recordsToSend.shift();

      // Check for PushXConnection before sending the record
      await this.recordingHelperService.setupPushXEventListener();
      if (!this.pushXEventSubscription) {
        this.pushXEventSubscription = this.recordingHelperService.pushXEventSubject.subscribe( (event) => {
          this.handlePushXEvent(event).catch((e) => { throw e; });
        });
      }

      // Ingest a record in Kinesis and then wait for PushX event
      await this.recordingHelperService.uploadAudioPart(this.partitionKey, recordData).toPromise();
      this.recordUploadInProcess = false;

      if (this.recordsToSend.length !== 0) {
        this.sendRecords().catch((error) => {
          this.recordsToSend = [];
          this.getUserMediaErrorCallback(error);
        });
      }
    }
  }

  private convertBlobToBase64(blob) {
    return new Promise<string>((resolve,reject)=> {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        const tmpStr = reader.result.toString();
        resolve(tmpStr.substring(tmpStr.indexOf('base64,') + 7));
      };
      reader.onerror = error => reject(error);
    });
  }

  //Play Audio
  async playAudio(){
    this.audioPlayerService.pauseAudioPromptPlayer();
    this.activityViewHelperService.stopAllOtherAudiosOnPlayingOneAudio.next(this.attemptNumber);

    const updatedSignedURL = await this.activityViewHelperService.checkExpiryAndReturnUpdatedSignedURL(this.audioElement.src, {
      audioDuration: this.audioElement.duration,
      fetchSignedUrlForInterBucket: !this.isRecordingSaved
    });
    if (updatedSignedURL) {
      this.audioElement.src = updatedSignedURL;
    }

    this.recorderState = "playing";
    this.audioElement.play();
  }

  //Stop Audio
  stopPlayingAudio() {
    this.recorderState = "stopped";
    this.audioElement.pause();
    this.audioElement.currentTime = 0;
  }

  //Convert to format 00:00:00 from seconds
  public calculateRecordingTimer(t, metaDataLoaded = false) {
    if (metaDataLoaded && (this.recorderState === 'processing' || this.recorderState === 'saving' || this.recorderState === 'submitting')) {
      // Remove the loader and emit to parent
      this.recorderState = 'stopped';
      this.recordingStopped.emit(true);
    }
    var sec_num = parseInt(t, 10); // don't forget the second param
    var hours: any = Math.floor(sec_num / 3600);
    var minutes: any = Math.floor((sec_num - (hours * 3600)) / 60);
    var seconds: any = sec_num - (hours * 3600) - (minutes * 60);

    if (hours < 10) { hours = "0" + hours; }
    if (minutes < 10) { minutes = "0" + minutes; }
    if (seconds < 10) { seconds = "0" + seconds; }
    return hours + ':' + minutes + ':' + seconds;
  }

  async saveOrsubmitRecordedAudio(action: 'save' | 'submit', isUploadNeeded: boolean = true) {
    try{
      // Safe check on recorder state before starting the save/submit action
      if (['recording', 'processing', 'submitting', 'saving'].indexOf(this.recorderState) > -1) {
        return;
      }
      //During save/submit stop the audio
      this.stopPlayingAudio();

      const userData = this.authService.getUserData();
      const classId = this.activityViewHelperService.getClass();
      let audioUrl = `${this.productMeta.paths['public-submissions']}/${userData.extUserId}/${this.activityId.replace(/\//g, '')}`
      if(classId) {
        audioUrl+= `/${classId}`
      }
      audioUrl += `/${this.attemptNumber}.mp3`; //cdn audio path`

      //Action can be either save or submit
      //Needed to show diff UI components and handling after save and submit
      if(action == 'submit') {
        this.audioPlayerService.pauseAudioPromptPlayer();
        this.recorderState = 'submitting';
      } else if(action == 'save') {
        this.audioPlayerService.pauseAudioPromptPlayer();
        this.recorderState = 'saving';
      }
      if(isUploadNeeded && !this.isRecordingSaved) {
        // Check for PushXConnection before sending the record
        await this.recordingHelperService.setupPushXEventListener();
        if (!this.pushXEventSubscription) {
          this.pushXEventSubscription = this.recordingHelperService.pushXEventSubject.subscribe( (event) => {
            this.handlePushXEvent(event).catch((e) => { throw e; });
          });
        }

        // Ingest a record to Kinesis to move the recording to submission bucket AND wait for PushX Event
        const recordData = JSON.stringify({ M: { I: ++this.iterationCounter, FE: this.recordingFileExtension, SB: 1, OID: this.orgId } });
        await this.recordingHelperService.uploadAudioPart(this.partitionKey, recordData).toPromise();
      } else {
        this.recordingSavedOrSubmitted.emit({ success: true, action, audioUrl });
      }
    } catch (error) {
      this.recordingSavedOrSubmitted.emit({ success: false, action });
    }

  }

  //Used to set recorder state from parent
  public setRecorderState(recorderState) {
    this.recorderState = "stopped";
  }

  //Used to set Audio Source from parent
  public setAudioSrc(signedAudioURL) {
    this.isRecordingSaved = true;
    this.audioElement.src = signedAudioURL;
  }

  private async handlePushXEvent(event: any) {
    if (event.subscriptionError &&
      (['processing', 'saving'].indexOf(this.recorderState) > -1 || (this.recorderState === 'submitting' && !this.isRecordingSaved))
    ) {
      // If PushX error occurs while one of the above action is going on, show the error modal
      this.getUserMediaErrorCallback(event.error);
      return;
    }

    if (event.category !== 'CUSTOM_AUDIO_STREAM_PROCESSING' || event.partitionKey !== this.partitionKey) {
      // Not our event, so skip this event
      return;
    }

    if (event.error) {
      // Handle errors
      this.getUserMediaErrorCallback(event.error);
    }

    if (event.conversion) {
      if (this.recorderState !== 'processing') {
        this.stopRecording();
        this.receivedLastChunk = true;
        this.recorderState = 'processing';
      }
      // For stop recording case
      const urlValidityPeriod = this.activityViewHelperService.getUrlValidityPeriod(this.recordedAudioDuration);
      const res = await this.recordingHelperService.getSignedUrlsForRecordingInInterBucket('submissions/' + this.partitionKey + '.mp3', urlValidityPeriod).toPromise();
      this.audioElement.src = res.signedUrl;
    } else if ((this.recorderState === 'submitting' || this.recorderState === 'saving') && event.savedToSubmissionBucket) {
      // For save/submit recording case
      const userData = this.authService.getUserData();
      let audioUrl = `${this.productMeta.paths['public-submissions']}/${userData.extUserId}/${this.activityId.replace(/\//g, '')}`
      const classId = this.activityViewHelperService.getClass();
      if(classId) {
        audioUrl+= `/${classId}`
      }
      audioUrl += `/${this.attemptNumber}.mp3`;
      this.recordingSavedOrSubmitted.emit({ success: true, action: this.recorderState === 'submitting' ? 'submit' : 'save', audioUrl });
      this.isRecordingSaved = true;
    }

    if (this.pushXEventSubscription) {
      this.pushXEventSubscription.unsubscribe();
      this.pushXEventSubscription = undefined;
    }
  }

  ngOnDestroy() {
    if(this.stopAllOtherAudiosOnPlayingOneAudioSubscription) {
      this.stopAllOtherAudiosOnPlayingOneAudioSubscription.unsubscribe();
    }
    if(this.stopOtherAudioActivitiesSubscription) {
      this.stopOtherAudioActivitiesSubscription.unsubscribe();
    }

    // Destroy the Audio element
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeEventListener('loadedmetadata', this.metadataLoadedListener);
      this.audioElement.removeEventListener('timeupdate', this.timeUpdateListener);      
      this.audioElement.src = '';
      this.audioElement = null;
    }

    this.resetState();
    this.recordingHelperService.pushXCleanup();
    if (this.pushXEventSubscription) {
      this.pushXEventSubscription.unsubscribe();
      this.pushXEventSubscription = undefined;
    }
    this.componentDestroyed = true;
  }
  
}
